import { passkeyErrorMessage } from '../lib/passkeyErrorMessage';
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  getSession, isAuthTrialMember, isDeviceTrusted, getTrustedUntil, trustThisDevice,
  forgetThisDevice, isPasskeySupported, getAccessToken, startPasskeyRegistration,
  completePasskeyRegistration, creationOptionsFromJSON, credentialToJSON,
  getSignedInEmail, setPasskeyHint, removePasskeyHint, listPasskeys, deletePasskey,
  signOutEverywhere, type Passkey,
} from '@darice/auth-client';
import { postSignIn } from '../lib/postSignIn';

export default function Security() {
  const location = useLocation();
  const navigate = useNavigate();
  const eligible = Boolean(getSession()) && isAuthTrialMember();
  const [trusted, setTrusted] = useState(() => eligible && isDeviceTrusted());
  const [supported, setSupported] = useState(false);
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  useEffect(() => {
    if (!eligible) return;
    let active = true;
    void isPasskeySupported().then(value => { if (active) setSupported(value); });
    void getAccessToken().then(listPasskeys).then(value => { if (active) setPasskeys(value); })
      .catch(() => { if (active) setError('We could not load your passkeys. Please try again.'); });
    return () => { active = false; };
  }, [eligible]);
  if (!eligible) return <Navigate to="/" replace />;

  async function run(action: () => Promise<void>) {
    setBusy(true); setError(''); setMessage('');
    try { await action(); }
    catch (error) { setError(passkeyErrorMessage(error, 'We could not finish that change. Please try again.')); }
    finally { setBusy(false); }
  }
  async function enroll() {
    const email = getSignedInEmail();
    if (!email) throw new Error('Please sign in again.');
    const accessToken = await getAccessToken();
    const options = await startPasskeyRegistration(accessToken);
    const credential = await navigator.credentials.create({ publicKey: creationOptionsFromJSON(options) });
    if (!credential) throw new DOMException('Passkey cancelled', 'NotAllowedError');
    await completePasskeyRegistration(accessToken, credentialToJSON(credential as PublicKeyCredential));
    setPasskeyHint(email);
    setMessage('Face ID or Touch ID is ready for your next sign-in.');
    setPasskeys(await listPasskeys(await getAccessToken()));
  }
  return <main className="auth-shell"><section className="signin-panel form-stack">
    <h1>Your sign-in settings</h1>
    <label><input type="checkbox" checked={trusted} disabled={busy} onChange={() => void run(async () => {
      if (trusted) forgetThisDevice(); else trustThisDevice();
      setTrusted(isDeviceTrusted());
    })} /> Keep me signed in on this device for 30 days</label>
    {trusted && <p>Trusted until {new Date(getTrustedUntil()!).toLocaleDateString()}.</p>}
    {supported && <button className="primary-action" disabled={busy} onClick={() => void run(enroll)}>Set up Face ID or Touch ID</button>}
    <h2>Your passkeys</h2>
    {passkeys.map(passkey => <div key={passkey.credentialId}>
      <span>{passkey.friendlyName}</span>{' '}
      <button className="secondary-action" disabled={busy} aria-label={`Remove ${passkey.friendlyName}`} onClick={() => void run(async () => {
        await deletePasskey(await getAccessToken(), passkey.credentialId);
        removePasskeyHint();
        setPasskeys(value => value.filter(item => item.credentialId !== passkey.credentialId));
        setMessage('Passkey removed. You can still use an email code.');
      })}>Remove</button>
    </div>)}
    <p>You can always sign in with an email code.</p>
    {confirmSignOut ? <div>
      <p>Sign out of all devices?</p>
      <button className="primary-action" disabled={busy} onClick={() => void run(signOutEverywhere)}>Yes, sign out everywhere</button>
      <button className="secondary-action" disabled={busy} onClick={() => setConfirmSignOut(false)}>Cancel</button>
    </div> : <button className="secondary-action" disabled={busy} onClick={() => setConfirmSignOut(true)}>Sign out of all devices</button>}
    {message && <p role="status">{message}</p>}
    {error && <p className="auth-error" role="alert">{error}</p>}
    <button className="primary-action" disabled={busy} onClick={() => postSignIn(location.search, navigate, false)}>Continue</button>
  </section></main>;
}
